// 3rd
import { Model } from '@vuex-orm/core';
// models
import { Profile, Wallet, Company } from './index';

// utils
import {
    API_ENDPOINT_CHANGE_LOCALE,
    API_ENDPOINT_LOGIN,
    API_ENDPOINT_LOGOUT,
    API_ENDPOINT_POST_CONTACT,
    API_ENDPOINT_REFRESH_TOKEN,
    API_ENDPOINT_REST_PASSWORD,
    API_ENDPOINT_UPDATE_INTRO,
    LOADER_CHANGE_LOCALE,
    LOADER_REFRESH_TOKEN,
    LOADER_RESET_PASSWORD,
    LOADER_SUBMIT_CONTACT,
    LOADER_SUBMIT_LOGIN,
    LOADER_SUBMIT_LOGOUT,
    LOCAL_STORAGE_JWT,
    LOCAL_STORAGE_TOKEN_EXPIRES,
    LOCAL_STORAGE_USER,
    API_ENDPOINT_SUBMIT_DEVICE,
    API_ENDPOINT_RESEND_TWO_FACTOR,
    LOADER_RESEND_TWO_FACTOR,
    LOADER_SUBMIT_CHANGE_DATA,
    API_ENDPOINT_POST_CHANGE_DATA,
    LOADER_SUBMIT_CHANGE_COMPANY_DATA,
    API_ENDPOINT_POST_CHANGE_COMPANY_DATA
} from '../utils/constants';
import { api, authHeaders, errorHandler } from '../utils/api';
// plugins
import { i18n } from '../plugins/vuetify';

// model
export class User extends Model {
    static entity = 'users';

    static fields() {
        return {
            id: this.attr(null),

            gender: this.attr(''),

            first_name: this.attr(''),
            last_name: this.attr(''),
            email: this.attr(''),
            participant_number: this.attr(''),

            locale: this.attr(''),
            country_code: this.attr(''),

            role: this.attr(''),

            approved: this.boolean(false),
            declined: this.boolean(false),

            intro: this.boolean(false),

            terms_and_conditions: this.attr(false),
            data_protection_agreement: this.attr(false),

            email_verified_at: this.attr(''),
            can_claim_contracts: this.boolean(false),

            // reference ids
            company_id: this.attr(null),
            wallet_id: this.attr(null),
            profile_id: this.attr(null),

            // references
            wallet: this.belongsTo(Wallet, 'wallet_id'),
            profile: this.belongsTo(Profile, 'profile_id'),
            company: this.belongsTo(Company, 'company_id'),

            // meta
            created_at: this.attr(''),
            updated_at: this.attr(''),

            device_tokens: this.attr(null),

            languages: this.attr(null)
        };
    }

    // api calls
    static async setLocale(value) {
        User.store().dispatch('wait/start', LOADER_CHANGE_LOCALE);

        const response = await api
            .post(API_ENDPOINT_CHANGE_LOCALE, { locale: value }, authHeaders())
            .catch(errorHandler);
        if (!response) return;

        const { data } = response;
        const { translations } = data;

        i18n.setLocaleMessage(value, { $vuetify: translations });

        User.store().dispatch('setBoolean', {
            stateToSet: 'init',
            value: false
        });

        await User.store()
            .dispatch('fetchInit', { skipRedirects: true })
            .catch(console.error);
        await User.store()
            .dispatch('fetchBootstrap')
            .catch(console.error);

        User.store().dispatch('wait/end', LOADER_CHANGE_LOCALE);
    }

    static async resetPassword(form) {
        User.store().dispatch('wait/start', LOADER_RESET_PASSWORD);

        const response = await api.post(API_ENDPOINT_REST_PASSWORD, form);

        User.store().dispatch('wait/end', LOADER_RESET_PASSWORD);

        return response;
    }

    static async resendTwoFactor(form) {
        // resend-two-factor-code
        User.store().dispatch('wait/start', LOADER_RESEND_TWO_FACTOR);

        // add current locale to form
        form.locale = i18n.locale;

        const request = await api.post(API_ENDPOINT_RESEND_TWO_FACTOR, form);
        if (!request) return request;

        User.store().dispatch('wait/end', LOADER_RESEND_TWO_FACTOR);
    }

    static async login(form) {
        await User.store().dispatch('wait/start', LOADER_SUBMIT_LOGIN);

        // add current locale to form
        form.locale = i18n.locale;

        const login = await api.post(API_ENDPOINT_LOGIN, form);

        if (!login) return login;

        const {
            data: { user, access_token, expires_timestamp, two_factor_code }
        } = login;

        if (two_factor_code) {
            await User.store().dispatch('openTwoFactorForm');
        } else {
            localStorage.setItem(LOCAL_STORAGE_USER, user.id);
            localStorage.setItem(
                LOCAL_STORAGE_TOKEN_EXPIRES,
                expires_timestamp
            );
            localStorage.setItem(LOCAL_STORAGE_JWT, access_token);
        }

        await User.store().dispatch('wait/end', LOADER_SUBMIT_LOGIN);
    }

    static async logout() {
        await User.store().dispatch('closeTwoFactorForm');
        User.store().dispatch('wait/start', LOADER_SUBMIT_LOGOUT);

        const logout = await api
            .get(API_ENDPOINT_LOGOUT, authHeaders())
            .catch(errorHandler);

        User.store().dispatch('wait/end', LOADER_SUBMIT_LOGOUT);

        return logout;
    }

    static async refreshToken() {
        User.store().dispatch('wait/start', LOADER_REFRESH_TOKEN);

        const response = await api
            .get(API_ENDPOINT_REFRESH_TOKEN, authHeaders())
            .catch(errorHandler);
        if (!response) return;

        const {
            data: { user, access_token, expires_timestamp }
        } = response;

        localStorage.setItem(LOCAL_STORAGE_USER, user.id);
        localStorage.setItem(LOCAL_STORAGE_TOKEN_EXPIRES, expires_timestamp);
        localStorage.setItem(LOCAL_STORAGE_JWT, access_token);

        User.store().dispatch('wait/end', LOADER_REFRESH_TOKEN);

        return response;
    }

    static async submitContactForm(form) {
        User.store().dispatch('wait/start', LOADER_SUBMIT_CONTACT);

        // add current locale to form
        form.locale = i18n.locale;

        const contactData = await api.post(
            API_ENDPOINT_POST_CONTACT,
            form,
            authHeaders()
        );

        User.store().dispatch('wait/end', LOADER_SUBMIT_CONTACT);

        return contactData;
    }

    static async submitDevice(device) {
        const response = await api
            .post(API_ENDPOINT_SUBMIT_DEVICE, device, authHeaders())
            .catch(errorHandler);
        if (!response) return;
    }

    static async updateIntro() {
        const response = await api
            .post(API_ENDPOINT_UPDATE_INTRO, null, authHeaders())
            .catch(errorHandler);
        if (!response) return;

        const { data } = response;
        const { user } = data;

        User.insert({ data: user });
    }

    static updateBalance(newBalance) {
        const currentUser = this.getCurrent();
        Wallet.update({
            where: currentUser.wallet_id,
            data: {
                balance: Number(newBalance)
            }
        });
    }

    // getters
    static getCurrent() {
        const localUser = localStorage.getItem('user');
        return User.query()
            .whereId(Number(localUser))
            .withAll()
            .first();
    }

    static async submitChangeCompanyData(form) {
        Profile.store().dispatch(
            'wait/start',
            LOADER_SUBMIT_CHANGE_COMPANY_DATA
        );

        const changeData = await api
            .post(API_ENDPOINT_POST_CHANGE_COMPANY_DATA, form, authHeaders())
            .catch(errorHandler);

        const {
            data: { profile }
        } = changeData;

        User.update({
            where: profile.id,
            data: {
                ...profile
            }
        });

        Profile.update({
            where: profile.profile.id,
            data: {
                ...profile.profile
            }
        });

        Company.update({
            where: profile.company.id,
            data: {
                ...profile.company
            }
        });

        Profile.store().dispatch('wait/end', LOADER_SUBMIT_CHANGE_COMPANY_DATA);

        return changeData;
    }

    static async submitChangeData(form) {
        Profile.store().dispatch('wait/start', LOADER_SUBMIT_CHANGE_DATA);

        const changeData = await api
            .post(API_ENDPOINT_POST_CHANGE_DATA, form, authHeaders())
            .catch(errorHandler);

        const {
            data: { profile }
        } = changeData;

        User.update({
            where: profile.id,
            data: {
                ...profile
            }
        });

        Profile.update({
            where: profile.profile.id,
            data: {
                ...profile.profile
            }
        });

        Profile.store().dispatch('wait/end', LOADER_SUBMIT_CHANGE_DATA);

        return changeData;
    }
}

export default User;
